import jsPDF from 'jspdf';
import { TylerState } from '../utils/TylerState';
import { CareerComparisonResults, CareerCashFlow } from '../types/careerComparison';

// CareerPathData type from CareerComparisonCalculator
interface CareerPathData {
  currentSalary: number;
  nonSalaryBenefits: number;
  currentAge: number;
  retirementAge: number;
  province: string;
  salaryGrowth: any;
  benefitsGrowthRate: number;
  hasEducation: boolean;
  yearsInSchool: number;
  annualSchoolCost: number;
  expectedStartingSalary: number;
  incomeDuringSchool: number;
  monthsAfterSchoolToStartJob: number;
  scenarioName: string;
}

export class CareerPDFService {
  // Professional financial statement formatting constants
  private readonly MARGIN_INCH = 25.4; // 1 inch in mm
  private readonly PAGE_WIDTH = 210; // A4 width in mm
  private readonly PAGE_HEIGHT = 297; // A4 height in mm

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  private formatCurrencyDetailed(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatPercent(amount: number): string {
    return `${amount.toFixed(2)}%`;
  }

  // Professional header with company branding (matching car/housing)
  private addHeader(pdf: jsPDF, scenarioName?: string): number {
    const margin = this.MARGIN_INCH;
    const headerHeight = 20;

    // Red background header bar
    pdf.setFillColor(220, 38, 38); // #dc2626 - website red
    pdf.rect(0, 0, this.PAGE_WIDTH, headerHeight, 'F');

    // Company name - white on red
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('times', 'bold');
    pdf.text('Larry\'s Help', margin, 12);

    // Website URL - small, right aligned
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.text('LarrysHelp.org', this.PAGE_WIDTH - margin, 12, { align: 'right' });

    // Reset to black text
    pdf.setTextColor(0, 0, 0);

    // Scenario name as title (if provided)
    let yPos = headerHeight + 12;
    if (scenarioName) {
      pdf.setFontSize(16);
      pdf.setFont('times', 'bold');
      pdf.text(scenarioName, margin, yPos);
      yPos += 8;
    }

    // Subtitle
    pdf.setFontSize(11);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60); // Dark gray
    pdf.text('Career Path Financial Analysis', margin, yPos);

    // Thin underline
    pdf.setDrawColor(220, 38, 38); // Match header red
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos + 2, this.PAGE_WIDTH - margin, yPos + 2);

    pdf.setTextColor(0, 0, 0); // Reset to black
    return yPos + 10; // Return starting Y position after header
  }

  // Professional footer with page numbers
  private addFooter(pdf: jsPDF, pageNum: number, totalPages: number, reportDate: string): void {
    const margin = this.MARGIN_INCH;

    // Bottom border line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, this.PAGE_HEIGHT - margin + 5, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 5);

    // Prepared by (left)
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Prepared by Larry\'s Help', margin, this.PAGE_HEIGHT - margin + 10);

    // Date (center)
    pdf.text(reportDate, this.PAGE_WIDTH / 2, this.PAGE_HEIGHT - margin + 10, { align: 'center' });

    // Page number (right)
    pdf.text(`Page ${pageNum} of ${totalPages}`, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 10, { align: 'right' });
  }

  // Professional section heading with underline
  private addSectionTitle(pdf: jsPDF, title: string, y: number): number {
    const margin = this.MARGIN_INCH;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('times', 'bold');
    pdf.text(title, margin, y);

    // Underline the section title
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    const textWidth = pdf.getTextWidth(title);
    pdf.line(margin, y + 1, margin + textWidth, y + 1);

    pdf.setFont('times', 'normal');
    return y + 8;
  }

  // Add a key-value pair in financial statement format
  private addKeyValue(pdf: jsPDF, label: string, value: string, y: number, indent: number = 0): number {
    const margin = this.MARGIN_INCH + indent;
    const valueX = this.PAGE_WIDTH - this.MARGIN_INCH - 5;

    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    pdf.text(label, margin, y);
    pdf.text(value, valueX, y, { align: 'right' });

    return y + 5;
  }

  // Add a table with professional financial statement styling
  private addTable(
    pdf: jsPDF,
    headers: string[],
    rows: (string | number)[][],
    yStart: number,
    columnWidths: number[]
  ): number {
    const margin = this.MARGIN_INCH;
    let y = yStart;

    // Header row with underline
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10);
    let x = margin;
    headers.forEach((header, i) => {
      if (i === 0) {
        pdf.text(header, x, y); // Left align first column
      } else {
        pdf.text(header, x + columnWidths[i] - 5, y, { align: 'right' }); // Right align numbers
      }
      x += columnWidths[i];
    });

    // Underline header
    pdf.setLineWidth(0.3);
    pdf.line(margin, y + 1, margin + columnWidths.reduce((a, b) => a + b, 0), y + 1);
    y += 6;

    // Data rows
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    rows.forEach((row, rowIndex) => {
      x = margin;
      row.forEach((cell, i) => {
        const cellText = typeof cell === 'number' ? this.formatCurrency(cell) : cell;
        if (i === 0) {
          pdf.text(cellText, x, y); // Left align first column
        } else {
          pdf.text(cellText, x + columnWidths[i] - 5, y, { align: 'right' }); // Right align numbers
        }
        x += columnWidths[i];
      });
      y += 5;

      // Add line after last row (total)
      if (rowIndex === rows.length - 1) {
        pdf.setLineWidth(0.5);
        pdf.line(margin, y + 0.5, margin + columnWidths.reduce((a, b) => a + b, 0), y + 0.5);
        y += 3;
      }
    });

    return y + 5;
  }

  // Main PDF generation function - reads from TylerState (matching car/housing pattern)
  // eslint-disable-next-line max-lines-per-function, max-statements
  generateCareerComparisonPDF(
    _unusedCurrentCareer: CareerPathData | null,
    _unusedNewCareer: CareerPathData | null,
    _unusedResults: CareerComparisonResults | null,
    _unusedCashFlowData?: any
  ): void {
    try {
      // Read all data from TylerState instead of props (like car/housing)
      const careerAInputs = TylerState.career.careerA.inputs;
      const careerAOutputs = TylerState.career.careerA.outputs;
      const careerBInputs = TylerState.career.careerB.inputs;
      const careerBOutputs = TylerState.career.careerB.outputs;
      const comparison = TylerState.career.comparison;

      if (!careerAInputs || !careerAOutputs || !careerBInputs || !careerBOutputs || !comparison) {
        console.error('Career data not found in TylerState');
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = this.MARGIN_INCH;
      const contentWidth = this.PAGE_WIDTH - (2 * margin);
      const reportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Calculate total pages
      let totalPages = 4; // Summary, Career A Detail, Career B Detail, Disclaimer
      let currentPage = 1;

      const scenarioTitle = `${careerAInputs.scenarioName || 'Career A'} vs ${careerBInputs.scenarioName || 'Career B'}`;

      // ===========================================
      // PAGE 1: SUMMARY
      // ===========================================
      let yPosition = this.addHeader(pdf, scenarioTitle);
      yPosition += 5;

      // Analysis period
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('Analysis Period:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(`Ages ${careerAInputs.currentAge} to ${careerAInputs.retirementAge}`, margin + 35, yPosition);
      yPosition += 15;

      // Comparison Summary
      yPosition = this.addSectionTitle(pdf, 'CAREER COMPARISON SUMMARY', yPosition);
      yPosition += 3;

      const isTie = comparison.winner === 'Tie';
      const winner = comparison.winner === 'A' ? careerAInputs.scenarioName : careerBInputs.scenarioName;
      const winnerEarnings = comparison.winner === 'A' ? careerAOutputs.netValue : careerBOutputs.netValue;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      const recommendText = isTie
        ? `Both paths end up within rounding of each other, at ${this.formatCurrency(careerAOutputs.netValue)} in net lifetime earnings.`
        : `${winner} provides ${this.formatCurrency(comparison.difference)} more in net lifetime earnings, with total net value of ${this.formatCurrency(winnerEarnings)}.`;
      const lines = pdf.splitTextToSize(recommendText, contentWidth);
      lines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });

      if (comparison.breakevenYear > 0 && comparison.breakevenAge < careerAInputs.retirementAge) {
        yPosition += 3;
        pdf.text(`Break-even point: Age ${comparison.breakevenAge} (year ${comparison.breakevenYear})`, margin, yPosition);
        yPosition += 5;
      } else {
        yPosition += 3;
      }

      // What the schooling actually costs, stated before any recommendation is acted on
      const schoolingCost = Math.max(careerAOutputs.netEducationCost || 0, careerBOutputs.netEducationCost || 0);
      if (schoolingCost > 0) {
        pdf.text(`Out of pocket for school: ${this.formatCurrency(schoolingCost)}`, margin, yPosition);
        yPosition += 5;
      }
      yPosition += 5;

      // Financial Summary Table
      yPosition = this.addSectionTitle(pdf, 'LIFETIME FINANCIAL SUMMARY', yPosition);
      yPosition += 3;

      const summaryRows: (string | number)[][] = [
        [careerAInputs.scenarioName || 'Career A', careerAOutputs.lifetimeEarnings, careerAOutputs.totalTaxes, careerAOutputs.netValue],
        [careerBInputs.scenarioName || 'Career B', careerBOutputs.lifetimeEarnings, careerBOutputs.totalTaxes, careerBOutputs.netValue]
      ];

      yPosition = this.addTable(
        pdf,
        ['Career Path', 'Gross Earnings', 'Total Taxes', 'Net Earnings'],
        summaryRows,
        yPosition,
        [46, 38, 38, 37]
      );

      yPosition += 5;

      // Career Path Comparison
      yPosition = this.addSectionTitle(pdf, 'CAREER PATH DETAILS', yPosition);
      yPosition += 3;

      const startSalaryA = careerAInputs.hasEducation ? careerAInputs.expectedStartingSalary : careerAInputs.currentSalary;
      const startSalaryB = careerBInputs.hasEducation ? careerBInputs.expectedStartingSalary : careerBInputs.currentSalary;

      const detailRows: (string | number)[][] = [
        ['Starting Salary', startSalaryA, startSalaryB],
        ['Working Years', careerAOutputs.workingYears + ' years', careerBOutputs.workingYears + ' years'],
        ['Education Years', careerAInputs.yearsInSchool + ' years', careerBInputs.yearsInSchool + ' years'],
        ['Education Cost', careerAInputs.annualSchoolCost * careerAInputs.yearsInSchool, careerBInputs.annualSchoolCost * careerBInputs.yearsInSchool],
        ['Average Annual (After Tax)', careerAOutputs.averageAnnualAfterTax, careerBOutputs.averageAnnualAfterTax]
      ];

      yPosition = this.addTable(
        pdf,
        ['Metric', careerAInputs.scenarioName || 'Career A', careerBInputs.scenarioName || 'Career B'],
        detailRows,
        yPosition,
        [60, 50, 50]
      );

      yPosition += 5;

      // Notes section
      yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS', yPosition);
      yPosition += 3;

      pdf.setFontSize(9);
      const notes = [
        `Tax calculations based on ${careerAInputs.province} provincial and federal tax rates`,
        `Benefits included in gross earnings calculation`,
        `All amounts in Canadian dollars (CAD)`,
        `Analysis assumes consistent tax rates throughout career`,
        `Education costs reduce net earnings during school years`,
        `Salary growth applied as specified in input parameters`
      ];

      notes.forEach(note => {
        if (yPosition > this.PAGE_HEIGHT - 50) {
          this.addFooter(pdf, currentPage, totalPages, reportDate);
          pdf.addPage();
          currentPage++;
          yPosition = this.addHeader(pdf, scenarioTitle);
          yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS (continued)', yPosition);
          yPosition += 3;
        }

        pdf.text(`• ${note}`, margin, yPosition);
        yPosition += 5;
      });

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 2: CAREER A DETAIL
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, scenarioTitle);

      yPosition = this.addSectionTitle(pdf, `${careerAInputs.scenarioName || 'CAREER A'} - DETAILED ANALYSIS`, yPosition);
      yPosition += 5;

      // Career Details
      yPosition = this.addSectionTitle(pdf, 'Career Details', yPosition);
      yPosition += 3;

      const careerADetailRows: (string | number)[][] = [
        ['Starting salary', startSalaryA],
        ['Starting age', careerAInputs.currentAge],
        ['Retirement age', careerAInputs.retirementAge],
        ['Working years', careerAOutputs.workingYears]
      ];

      if (careerAInputs.hasEducation) {
        careerADetailRows.push(['Education years', careerAInputs.yearsInSchool]);
        careerADetailRows.push(['Annual education cost', careerAInputs.annualSchoolCost]);
        careerADetailRows.push(['Total education cost', careerAInputs.annualSchoolCost * careerAInputs.yearsInSchool]);
      }

      yPosition = this.addTable(pdf, ['Description', 'Value'], careerADetailRows, yPosition, [106, 53]);

      // Financial Summary
      yPosition += 3;
      yPosition = this.addSectionTitle(pdf, 'Lifetime Financial Summary', yPosition);
      yPosition += 3;

      const careerASummaryRows: (string | number)[][] = [
        ['Total gross earnings', careerAOutputs.lifetimeEarnings],
        ['Total taxes paid', careerAOutputs.totalTaxes],
        ['Total benefits', careerAOutputs.totalBenefits],
        ['Net lifetime earnings', careerAOutputs.netValue],
        ['Average annual (before tax)', careerAOutputs.averageAnnualBeforeTax],
        ['Average annual (after tax)', careerAOutputs.averageAnnualAfterTax]
      ];

      yPosition = this.addTable(pdf, ['Item', 'Amount'], careerASummaryRows, yPosition, [106, 53]);

      // Year-by-year cash flows (first 10 years of working)
      if (careerAOutputs.cashFlows && careerAOutputs.cashFlows.length > 0) {
        yPosition += 5;
        yPosition = this.addSectionTitle(pdf, 'Year-by-Year Cash Flows (First 10 Working Years)', yPosition);
        yPosition += 3;

        // Filter to working years only and take first 10
        const workingFlows = careerAOutputs.cashFlows.filter((f: any) => f.period === 'Working').slice(0, 10);
        const yearlyRows: (string | number)[][] = workingFlows.map((flow: any) => [
          `Age ${flow.age}`,
          flow.grossEarnings,
          flow.taxes,
          flow.netIncome
        ]);

        yPosition = this.addTable(
          pdf,
          ['Age', 'Gross', 'Taxes', 'Net'],
          yearlyRows,
          yPosition,
          [20, 40, 40, 40]
        );

        if (careerAOutputs.cashFlows.filter((f: any) => f.period === 'Working').length > 10) {
          pdf.setFontSize(8);
          pdf.text(`Note: Showing first 10 of ${careerAOutputs.cashFlows.filter((f: any) => f.period === 'Working').length} working years`, margin, yPosition);
          yPosition += 5;
        }
      }

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 3: CAREER B DETAIL
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, scenarioTitle);

      yPosition = this.addSectionTitle(pdf, `${careerBInputs.scenarioName || 'CAREER B'} - DETAILED ANALYSIS`, yPosition);
      yPosition += 5;

      // Career Details
      yPosition = this.addSectionTitle(pdf, 'Career Details', yPosition);
      yPosition += 3;

      const careerBDetailRows: (string | number)[][] = [
        ['Starting salary', startSalaryB],
        ['Starting age', careerBInputs.currentAge],
        ['Retirement age', careerBInputs.retirementAge],
        ['Working years', careerBOutputs.workingYears]
      ];

      if (careerBInputs.hasEducation) {
        careerBDetailRows.push(['Education years', careerBInputs.yearsInSchool]);
        careerBDetailRows.push(['Annual education cost', careerBInputs.annualSchoolCost]);
        careerBDetailRows.push(['Total education cost', careerBInputs.annualSchoolCost * careerBInputs.yearsInSchool]);
      }

      yPosition = this.addTable(pdf, ['Description', 'Value'], careerBDetailRows, yPosition, [106, 53]);

      // Financial Summary
      yPosition += 3;
      yPosition = this.addSectionTitle(pdf, 'Lifetime Financial Summary', yPosition);
      yPosition += 3;

      const careerBSummaryRows: (string | number)[][] = [
        ['Total gross earnings', careerBOutputs.lifetimeEarnings],
        ['Total taxes paid', careerBOutputs.totalTaxes],
        ['Total benefits', careerBOutputs.totalBenefits],
        ['Net lifetime earnings', careerBOutputs.netValue],
        ['Average annual (before tax)', careerBOutputs.averageAnnualBeforeTax],
        ['Average annual (after tax)', careerBOutputs.averageAnnualAfterTax]
      ];

      yPosition = this.addTable(pdf, ['Item', 'Amount'], careerBSummaryRows, yPosition, [106, 53]);

      // Year-by-year cash flows (first 10 years of working)
      if (careerBOutputs.cashFlows && careerBOutputs.cashFlows.length > 0) {
        yPosition += 5;
        yPosition = this.addSectionTitle(pdf, 'Year-by-Year Cash Flows (First 10 Working Years)', yPosition);
        yPosition += 3;

        // Filter to working years only and take first 10
        const workingFlows = careerBOutputs.cashFlows.filter((f: any) => f.period === 'Working').slice(0, 10);
        const yearlyRows: (string | number)[][] = workingFlows.map((flow: any) => [
          `Age ${flow.age}`,
          flow.grossEarnings,
          flow.taxes,
          flow.netIncome
        ]);

        yPosition = this.addTable(
          pdf,
          ['Age', 'Gross', 'Taxes', 'Net'],
          yearlyRows,
          yPosition,
          [20, 40, 40, 40]
        );

        if (careerBOutputs.cashFlows.filter((f: any) => f.period === 'Working').length > 10) {
          pdf.setFontSize(8);
          pdf.text(`Note: Showing first 10 of ${careerBOutputs.cashFlows.filter((f: any) => f.period === 'Working').length} working years`, margin, yPosition);
          yPosition += 5;
        }
      }

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 4: DISCLAIMER
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, scenarioTitle);

      yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER', yPosition);
      yPosition += 3;

      const disclaimerText = [
        'This career analysis is provided for educational and informational purposes only. The calculations and projections are estimates based on the information you provided and certain assumptions about future events.',
        '',
        'Key Limitations:',
        '• Tax rates may change over time',
        '• Salary growth projections are estimates',
        '• Benefits and compensation structures may vary',
        '• Does not account for inflation or time value of money',
        '• Provincial and federal tax calculations are simplified',
        '• This analysis does not constitute financial or career advice',
        '',
        'Recommendations:',
        '• Consult with qualified financial and career advisors',
        '• Consider non-financial factors (job satisfaction, work-life balance, etc.)',
        '• Review current labor market conditions and industry trends',
        '• Factor in additional career costs (commuting, professional development, etc.)',
        '• Evaluate the impact of career changes on benefits and retirement savings',
        '',
        'Larry\'s Help provides financial planning tools to help you make informed decisions, but the ultimate responsibility for career and financial decisions rests with you.'
      ];

      pdf.setFontSize(9);
      pdf.setFont('times', 'normal');

      disclaimerText.forEach((text) => {
        if (yPosition > this.PAGE_HEIGHT - 60) {
          this.addFooter(pdf, currentPage, totalPages, reportDate);
          pdf.addPage();
          currentPage++;
          totalPages++;
          yPosition = this.addHeader(pdf, scenarioTitle);
          yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER (continued)', yPosition);
          yPosition += 3;
        }

        if (text === '') {
          yPosition += 4;
        } else {
          const maxWidth = contentWidth - 10;
          const lines = pdf.splitTextToSize(text, maxWidth);
          lines.forEach((line: string) => {
            if (yPosition > this.PAGE_HEIGHT - 60) {
              this.addFooter(pdf, currentPage, totalPages, reportDate);
              pdf.addPage();
              currentPage++;
              totalPages++;
              yPosition = this.addHeader(pdf, scenarioTitle);
              yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER (continued)', yPosition);
              yPosition += 3;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 6;
          });
        }
      });

      // Add final footer
      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // Generate filename and download
      const filename = `Career_Comparison_${(careerAInputs.scenarioName || 'CareerA').replace(/\s+/g, '_')}_vs_${(careerBInputs.scenarioName || 'CareerB').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

    } catch (error) {
      console.error('Error generating Career PDF:', error);
    }
  }
}

const careerPDFService = new CareerPDFService();
export default careerPDFService;
